import useLogoutUser from '@/modules/auth/hooks/useLogoutUser';
import useAuthUserStore from '@/modules/auth/stores/useAuthUserStore';
import type { FileRoutesByTo } from '@/modules/router/constants/routeTree.gen';
import {
  createFileRoute,
  Outlet,
  redirect,
  useCanGoBack,
  useRouter,
} from '@tanstack/react-router';
import { Button, ErrorPage } from '@thisisdmg/tirecloud-patterns/v2';
import { IconLogout } from '@thisisdmg/tirecloud-patterns/v2/components/icons';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_main-ui/_protected')({
  beforeLoad({ context, location }) {
    if (context.userState.status === 'unauthenticated')
      throw redirect({
        to: '/auth/login',
        search: {
          redirect: location.href as keyof FileRoutesByTo,
        },
      });
  },
  component: ProtectedRoute,
});

function ProtectedRoute() {
  const { t } = useTranslation();
  const user = useAuthUserStore((state) => state.user);
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();
  const logoutUser = useLogoutUser();
  const action = Route.useSearch({ select: (s) => s.action });

  if (user === undefined || user.role !== 'ROLE_ADMIN') {
    if (action === 'logout') return null;

    return (
      <ErrorPage
        code={[4, 0, 3]}
        title={t('errors.forbidden_title')}
        description={t('errors.forbidden_description')}
        actionButtons={
          <div className="tc:inline-flex tc:gap-x-2">
            <Button.Solid
              color="brand"
              onClick={() => {
                if (canGoBack) {
                  router.history.back();
                } else {
                  navigate({
                    to: '/',
                    search: { ref: 'forbidden' },
                    replace: true,
                  });
                }
              }}
            >
              {t('common.backToPreviousPage')}
            </Button.Solid>
            <Button.Solid
              icon={<IconLogout />}
              onClick={() => {
                logoutUser({ ref: 'forbidden' });
              }}
            >
              {t('common.logout')}
            </Button.Solid>
          </div>
        }
      />
    );
  }

  return <Outlet />;
}
